import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  User,
  Plus,
  Pencil,
  MagnifyingGlass,
  Phone,
  Buildings,
  WhatsappLogo,
  IdentificationCard,
  EnvelopeSimple,
  Motorcycle,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import { toast } from "sonner";
import ClientFormModal from "../components/ClientFormModal";
import { formatDocument } from "../lib/utils";
import ClientMotosModal from "@/components/ClientMotosModal";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [clientToEdit, setClientToEdit] = useState(null);

  const [viewingMotosFor, setViewingMotosFor] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(res.data);
    } catch (err) {
      toast.error("Erro ao carregar a lista de clientes.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNewClient = () => {
    setClientToEdit(null);
    setShowModal(true);
  };

  const handleEdit = (client) => {
    setClientToEdit(client);
    setShowModal(true);
  };

  const handleSuccess = (savedClient) => {
    setClients((prev) => {
      const exists = prev.find((p) => p.id === savedClient.id);
      if (exists) {
        return prev.map((p) => (p.id === savedClient.id ? savedClient : p));
      }
      return [savedClient, ...prev];
    });
  };

  const filteredClients = clients.filter((c) => {
    const search = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(search) ||
      c.document?.includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <Layout title="Clientes">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Clientes">
      <div className="space-y-4 pb-6">
        <div className="relative w-full">
          <MagnifyingGlass
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] shrink-0"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field !pl-12 h-12 w-full"
            placeholder="Buscar por nome, CPF/CNPJ ou e-mail..."
            data-testid="search-clients"
          />
        </div>

        {filteredClients.length === 0 ? (
          <div className="card p-8 text-center">
            <IdentificationCard
              size={48}
              className="mx-auto text-[#A1A1AA] mb-4"
            />
            <p className="text-[#A1A1AA] mb-2">Nenhum cliente encontrado.</p>
            {searchQuery && (
              <p className="text-xs text-white/40">
                Tente buscar por outro termo.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                className="card p-4 sm:p-5"
                data-testid={`client-${client.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                      {client.document_type === "cnpj" ? (
                        <Buildings
                          size={24}
                          className="text-orange-500 sm:w-6 sm:h-6"
                        />
                      ) : (
                        <User
                          size={24}
                          className="text-orange-500 sm:w-6 sm:h-6"
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-base sm:text-lg truncate">
                        {client.name}
                      </h3>

                      {client.document && (
                        <div className="flex items-center gap-1.5 mt-1 text-sm text-[#A1A1AA]">
                          <IdentificationCard size={14} className="shrink-0" />
                          <span className="font-mono truncate">
                            {formatDocument(client.document)}
                          </span>
                        </div>
                      )}

                      {client.phone && (
                        <div className="flex items-center gap-1.5 mt-1 text-sm text-[#A1A1AA]">
                          <Phone size={14} className="shrink-0" />
                          <span className="truncate">{client.phone}</span>
                        </div>
                      )}

                      {client.whatsapp && (
                        <div className="flex items-center gap-1.5 mt-1 text-sm text-[#00E676]">
                          <WhatsappLogo
                            weight="fill"
                            size={14}
                            className="shrink-0"
                          />
                          <span className="truncate">{client.whatsapp}</span>
                        </div>
                      )}

                      {client.email && (
                        <div className="flex items-center gap-1.5 mt-1.5 text-sm text-[#A1A1AA]">
                          <EnvelopeSimple size={14} className="shrink-0" />
                          <span className="truncate">{client.email}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 shrink-0 border-t border-white/5 sm:border-0 pt-3 sm:pt-0">
                    <button
                      onClick={() => setViewingMotosFor(client)}
                      className="w-10 h-10 sm:w-auto sm:px-4 rounded-lg bg-[#00E676]/10 border border-[#00E676]/20 flex items-center justify-center hover:bg-[#00E676]/20 transition-colors gap-2"
                      title="Ver Motos do Cliente"
                    >
                      <Motorcycle size={18} className="text-[#00E676]" />
                      <span className="hidden sm:inline text-sm text-[#00E676]">
                        Motos
                      </span>
                    </button>

                    <button
                      onClick={() => handleEdit(client)}
                      className="w-10 h-10 sm:w-auto sm:px-4 rounded-lg bg-[#1A1A1A] border border-white/10 flex items-center justify-center hover:bg-[#222] transition-colors gap-2"
                      title="Editar Cliente"
                    >
                      <Pencil size={18} className="text-[#A1A1AA]" />
                      <span className="hidden sm:inline text-sm text-[#A1A1AA]">
                        Editar
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={handleOpenNewClient}
          className="fixed bottom-24 right-4 w-14 h-14 bg-[#00E676] rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(0,230,118,0.3)] hover:bg-[#00C853] hover:scale-105 transition-all z-20"
          title="Cadastrar Novo Cliente"
        >
          <Plus size={28} weight="bold" className="text-black" />
        </button>
      </div>

      {showModal && (
        <ClientFormModal
          API={API}
          clientToEdit={clientToEdit}
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {viewingMotosFor && (
        <ClientMotosModal
          client={viewingMotosFor}
          API={API}
          onClose={() => setViewingMotosFor(null)}
        />
      )}
    </Layout>
  );
}
